import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { tr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ArrowLeft,
  Package,
  ShoppingCart,
  TrendingUp,
  Calendar,
  Edit,
  Trash2,
  ArrowLeftRight,
  X
} from 'lucide-react';
import { productsAPI, salesAPI, carTransfersAPI, stockMovementsAPI, carSalesAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate, useParams } from 'react-router-dom';

const ProductDetail = () => {
  const { user, userRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesStats, setSalesStats] = useState({
    totalSales: 0,
    totalQuantity: 0,
    totalRevenue: 0,
    averageSaleValue: 0
  });
  const [salesFilter, setSalesFilter] = useState('today');
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [salesLoading, setSalesLoading] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [salesSubmitting, setSalesSubmitting] = useState(false);

  // Stock movements state
  const [stockMovements, setStockMovements] = useState([]);
  const [stockMovementsLoading, setStockMovementsLoading] = useState(false);
  const [stockMovementSummary, setStockMovementSummary] = useState({ in: 0, out: 0 });

  // Edit product modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    product_code: '',
    brand: '',
    cost: '',
    price: '',
    stock: ''
  });
  const [stockToAdd, setStockToAdd] = useState('');

  // Sales form state
  const [salesFormData, setSalesFormData] = useState({
    productId: productId || '',
    quantity: '',
    unitPrice: '',
    totalPrice: ''
  });

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [carReturnOptions, setCarReturnOptions] = useState([]);
  const [selectedReturnCarId, setSelectedReturnCarId] = useState('');
  const [returnQuantity, setReturnQuantity] = useState('');
  const [returnLoading, setReturnLoading] = useState(false);
  const [carStockSummary, setCarStockSummary] = useState(0);
  const [carStockLoading, setCarStockLoading] = useState(false);

  // Reset sales form
  const resetSalesForm = useCallback(() => {
    setSalesFormData({
      productId: productId || '',
      quantity: '',
      unitPrice: '',
      totalPrice: ''
    });
    setEditingSale(null);
    setShowSalesModal(false);
  }, [productId]);

  // Reset edit form
  const resetEditForm = useCallback(() => {
    if (product) {
      setEditFormData({
        name: product.name || '',
        product_code: product.product_code || '',
        brand: product.brand || '',
        cost: product.cost || '',
        price: product.price || '',
        stock: product.stock || ''
      });
    }
    setStockToAdd('');
    setShowEditModal(false);
  }, [product]);

  // Handle edit form input change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle stock addition
  const handleAddStock = async () => {
    const addAmount = parseFloat(stockToAdd) || 0;
    if (addAmount <= 0) {
      showError('Lütfen 0\'dan büyük bir miktar girin');
      return;
    }

    try {
      console.log('Adding stock with amount:', addAmount, 'User role:', userRole);

      // Update stock and create movement record
      await productsAPI.updateStock(productId, addAmount, {
        movementType: 'in',
        referenceType: 'manual_adjustment',
        notes: `Manuel Stok Ekleme - ${addAmount} adet`,
        createdBy: user?.id
      });

      console.log('Stock update completed, refreshing data...');

      // Clear the input field
      setStockToAdd('');

      // Refresh data from server to get accurate stock values
      await fetchProduct();
      await fetchStockMovements();

      showSuccess(`${addAmount} adet stok eklendi`);
    } catch (error) {
      console.error('Add stock error:', error);
      showError('Stok eklenirken hata oluştu');
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate desired stock
      const desiredStockRaw = editFormData.stock;
      const desiredStock = Number.isFinite(parseInt(desiredStockRaw, 10)) ? parseInt(desiredStockRaw, 10) : 0;
      if (desiredStock < 0) {
        showError('Stok negatif olamaz');
        return;
      }

      // Fetch latest product to avoid stale stock calculations
      const latest = await productsAPI.getProduct(productId);
      const latestStock = parseInt(latest?.data?.stock ?? 0, 10) || 0;
      const stockDifference = desiredStock - latestStock;


      // Confirm when reducing stock
      if (stockDifference < 0) {
        const confirmMessage = `Stok ${Math.abs(stockDifference)} adet azaltılacak. Bu işlemi onaylıyor musunuz?`;
        if (!window.confirm(confirmMessage)) {
          return;
        }
      }

      // Apply stock delta via updateStock FIRST (to avoid race conditions)
      if (stockDifference !== 0) {
        try {

          await productsAPI.updateStock(productId, stockDifference, {
            movementType: stockDifference > 0 ? 'in' : 'out',
            referenceType: 'manual_adjustment',
            notes: `Stok Düzenleme - ${Math.abs(stockDifference)} adet ${stockDifference > 0 ? 'eklendi' : 'çıkarıldı'}`,
            createdBy: user?.id
          });

        } catch (movementError) {
          console.error('Stock movement creation error:', movementError);
        }
      }

      // Update non-stock fields AFTER stock update (exclude stock field completely)
      const { stock, ...productDataWithoutStock } = editFormData;
      await productsAPI.updateProduct(productId, productDataWithoutStock, user?.id, userRole);

      showSuccess('Ürün bilgileri başarıyla güncellendi!');
      resetEditForm();

      await fetchProduct();
      await fetchStockMovements();



    } catch (error) {
      console.error('Edit product error:', error);
      showError('Ürün güncellenirken hata oluştu');
    }
  };

  // Fetch product details
  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getProduct(productId);
      setProduct(response.data);

      // Set default unit price
      setSalesFormData(prev => ({
        ...prev,
        unitPrice: response.data.price || ''
      }));
    } catch (error) {
      console.error('Fetch product error:', error);
      showError('Ürün bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [productId, showError]);


  // Fetch sales history for this product
  const fetchSalesHistory = useCallback(async () => {
    try {
      setSalesLoading(true);

      // Fetch both stock sales and car sales
      const [stockSalesResponse, carSalesResponse] = await Promise.all([
        salesAPI.getSalesHistory({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          productId: productId
        }),
        carSalesAPI.getByProduct(productId, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
      ]);

      // Get sales for this product (already filtered by API)
      const stockSales = stockSalesResponse.data.sales || [];
      const carSales = carSalesResponse.data || [];

      // Combine both sales types
      const allSales = [...stockSales, ...carSales];
      setSalesHistory(allSales);

      // Calculate stats for this product (including car sales)
      const totalSales = allSales.length;
      const totalQuantity = allSales.reduce((sum, sale) => sum + (sale.quantity || 0), 0);
      const totalRevenue = allSales.reduce((sum, sale) => sum + (parseFloat(sale.total_price || sale.totalPrice) || 0), 0);
      const averageSaleValue = totalSales > 0 ? totalRevenue / totalSales : 0;

      setSalesStats({
        totalSales,
        totalQuantity,
        totalRevenue,
        averageSaleValue
      });
    } catch (error) {
      console.error('Fetch sales history error:', error);
      showError('Satış geçmişi yüklenemedi');
    } finally {
      setSalesLoading(false);
    }
  }, [startDate, endDate, productId, showError]);

  // Fetch stock movements for this product
  const fetchStockMovements = useCallback(async () => {
    try {
      setStockMovementsLoading(true);

      // Fetch stock movements, regular sales, and car sales data
      const [movementsResponse, salesResponse] = await Promise.all([
        stockMovementsAPI.getByProduct(productId, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }),
        salesAPI.getSalesHistory({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          productId: productId
        }),
        carSalesAPI.getByProduct(productId, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        })
      ]);

      const movements = movementsResponse.data || [];
      const sales = salesResponse.data?.sales || [];

      // Calculate summary from stock movements
      const movementsSummary = movements.reduce((acc, movement) => {
        if (movement.movement_type === 'in' || movement.movement_type === 'transfer_in') {
          acc.in += movement.quantity;
        } else if (movement.movement_type === 'out' || movement.movement_type === 'transfer_out') {
          acc.out += movement.quantity;
        }
        return acc;
      }, { in: 0, out: 0 });

      // Add only stock sales as outflows (car sales don't reduce main warehouse stock)
      // Stock is already reduced during transfer, so car sales are just for tracking
      const salesOutflow = sales.reduce((sum, sale) => sum + (sale.quantity || 0), 0);

      const totalSummary = {
        in: movementsSummary.in,
        out: movementsSummary.out + salesOutflow
      };

      setStockMovements(movements);
      setStockMovementSummary(totalSummary);
    } catch (error) {
      console.error('Fetch stock movements error:', error);
      showError('Stok hareketleri yüklenirken hata oluştu');
    } finally {
      setStockMovementsLoading(false);
    }
  }, [startDate, endDate, productId, showError]);

  const refreshCarStocks = useCallback(async () => {
    if (!productId) return [];

    try {
      setCarStockLoading(true);
      const response = await carTransfersAPI.getProductStocks(productId);
      const stocks = response.data?.stocks || [];
      const total = response.data?.total ?? stocks.reduce((sum, entry) => sum + (entry.quantity || 0), 0);
      setCarReturnOptions(stocks);
      setCarStockSummary(total);
      return stocks;
    } catch (error) {
      console.error('Araç stok bilgisi alınırken hata oluştu:', error);
      setCarReturnOptions([]);
      setCarStockSummary(0);
      showError('Araç stok bilgileri alınamadı');
      return [];
    } finally {
      setCarStockLoading(false);
    }
  }, [productId, showError]);

  // Handle filter change without page refresh
  const handleFilterChange = (newFilter) => {
    setSalesFilter(newFilter);

    // For custom filter, don't change dates - let user pick
    if (newFilter === 'custom') {
      return;
    }

    const today = new Date();
    let newStartDate = new Date(today);
    let newEndDate = new Date(today);

    // Set date range based on filter
    switch (newFilter) {
      case 'today':
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Get Monday of current week
        const day = today.getDay();
        const mondayOffset = day === 0 ? 6 : day - 1;
        newStartDate.setDate(today.getDate() - mondayOffset);
        newStartDate.setHours(0, 0, 0, 0);
        // Get Sunday of current week
        newEndDate.setDate(newStartDate.getDate() + 6);
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case 'month':
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        newEndDate.setHours(23, 59, 59, 999);
        break;
      default:
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate.setHours(23, 59, 59, 999);
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Handle start date change
  const handleStartDateChange = (date) => {
    if (!date) return;

    const newStartDate = new Date(date);
    newStartDate.setHours(0, 0, 0, 0);

    // If new start date is after end date, update end date to start date
    if (newStartDate > endDate) {
      const newEndDate = new Date(newStartDate);
      newEndDate.setHours(23, 59, 59, 999);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    } else {
      setStartDate(newStartDate);
    }
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    if (!date) return;

    const newEndDate = new Date(date);
    newEndDate.setHours(23, 59, 59, 999);

    // If new end date is before start date, update start date to end date
    if (newEndDate < startDate) {
      const newStartDate = new Date(newEndDate);
      newStartDate.setHours(0, 0, 0, 0);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    } else {
      setEndDate(newEndDate);
    }
  };

  // Handle sales form input change
  const handleSalesInputChange = (e) => {
    const { name, value } = e.target;
    setSalesFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calculate total price when quantity or unit price changes
    if (name === 'quantity' || name === 'unitPrice') {
      const quantity = name === 'quantity' ? parseFloat(value) || 0 : parseFloat(salesFormData.quantity) || 0;
      const unitPrice = name === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(salesFormData.unitPrice) || 0;
      const totalPrice = (quantity * unitPrice).toFixed(2);

      setSalesFormData(prev => ({
        ...prev,
        [name]: value,
        totalPrice: totalPrice
      }));
    }
  };

  // Handle edit sale
  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setSalesFormData({
      productId: sale.product_id,
      quantity: sale.quantity.toString(),
      unitPrice: sale.unit_price.toString(),
      totalPrice: sale.total_price.toString()
    });
    setShowSalesModal(true);
  };

  // Handle delete sale
  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Bu satışı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setSalesSubmitting(true);

      // Find the sale to get quantity
      const saleToDelete = salesHistory.find(sale => sale.id === saleId);
      if (!saleToDelete) {
        showError('Satış bulunamadı');
        return;
      }

      // Delete the sale and restore stock in backend
      await salesAPI.deleteSale(saleId, saleToDelete.quantity, productId);

      showSuccess('Satış başarıyla silindi ve stok geri eklendi!');
      fetchProduct(); // Refresh product data
      fetchSalesHistory(); // Refresh sales history
    } catch (error) {
      console.error('Delete sale error:', error);
      showError('Satış silinemedi. Lütfen tekrar deneyin.');
    } finally {
      setSalesSubmitting(false);
    }
  };

  // Handle delete car sale
  const handleDeleteCarSale = async (carSaleId) => {
    if (!window.confirm('Bu araç satışını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setSalesSubmitting(true);

      await carSalesAPI.deleteCarSale(carSaleId);

      showSuccess('Araç satışı başarıyla silindi!');
      fetchProduct(); // Refresh product data
      fetchSalesHistory(); // Refresh sales history
      refreshCarStocks(); // Refresh car stocks
    } catch (error) {
      console.error('Delete car sale error:', error);
      showError('Araç satışı silinemedi. Lütfen tekrar deneyin.');
    } finally {
      setSalesSubmitting(false);
    }
  };

  // Handle delete stock movement
  const handleDeleteStockMovement = async (movementId) => {
    if (!window.confirm('Bu stok hareketini silmek istediğinizden emin misiniz? Stok değeri geri alınacaktır.')) {
      return;
    }

    try {
      setSalesSubmitting(true);

      await stockMovementsAPI.deleteStockMovement(movementId);

      showSuccess('Stok hareketi başarıyla silindi ve stok güncellendi!');
      fetchProduct(); // Refresh product data
      fetchStockMovements(); // Refresh stock movements
    } catch (error) {
      console.error('Delete stock movement error:', error);
      showError('Stok hareketi silinemedi. Lütfen tekrar deneyin.');
    } finally {
      setSalesSubmitting(false);
    }
  };


  // Handle sales submission
  const handleSalesSubmit = async (e) => {
    e.preventDefault();
    try {
      setSalesSubmitting(true);

      if (!product) {
        showError('Ürün bulunamadı');
        return;
      }

      const quantity = parseFloat(salesFormData.quantity);
      const unitPrice = parseFloat(salesFormData.unitPrice);
      const totalPrice = parseFloat(salesFormData.totalPrice);

      if (quantity <= 0) {
        showError('Miktar 0\'dan büyük olmalıdır');
        return;
      }

      if (unitPrice <= 0) {
        showError('Birim fiyat 0\'dan büyük olmalıdır');
        return;
      }

      if (editingSale) {
        // Update existing sale
        const quantityDifference = quantity - editingSale.quantity;
        const newStock = product.stock - quantityDifference;

        if (newStock < 0) {
          showError('Yeterli stok yok. Mevcut stok: ' + product.stock);
          return;
        }

        // Update the sale record
        await salesAPI.updateSale(editingSale.id, {
          quantity: quantity,
          unitPrice: unitPrice,
          totalPrice: totalPrice
        });

        // Update product stock using updateStock to preserve cost and price
        await productsAPI.updateStock(productId, quantityDifference, {
          movementType: quantityDifference > 0 ? 'in' : 'out',
          referenceType: 'sale',
          notes: `Satış - ${Math.abs(quantityDifference)} adet`,
          createdBy: user?.id
        });

        showSuccess('Satış başarıyla güncellendi!');
      } else {
        // Create new sale
        if (quantity > product.stock) {
          showError('Yeterli stok yok. Mevcut stok: ' + product.stock);
          return;
        }

        const newStock = product.stock - quantity;

        await salesAPI.createSale({
          productId: productId,
          quantity: quantity,
          unitPrice: unitPrice,
          totalPrice: totalPrice,
          newStock: newStock
        }, user?.id);

        showSuccess('Satış başarıyla tamamlandı!');
      }

      resetSalesForm();
      fetchProduct(); // Refresh product data
      fetchSalesHistory(); // Refresh sales history
    } catch (error) {
      console.error('Sales error:', error);
      showError('Satış işlemi başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSalesSubmitting(false);
    }
  };

  const handleOpenReturnModal = useCallback(async () => {
    setShowReturnModal(true);
    const stocks = await refreshCarStocks();
    if (stocks.length > 0) {
      setSelectedReturnCarId(stocks[0].carId);
      setReturnQuantity('');
    } else {
      setSelectedReturnCarId('');
      setReturnQuantity('');
    }
  }, [refreshCarStocks]);

  const handleReturnModalClose = useCallback(() => {
    setShowReturnModal(false);
    setReturnQuantity('');
    setSelectedReturnCarId('');
  }, []);

  const handleReturnSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!selectedReturnCarId) {
      showError('Lütfen araç seçin');
      return;
    }

    const selectedCar = carReturnOptions.find(option => option.carId === selectedReturnCarId);
    const available = selectedCar ? selectedCar.quantity : 0;
    const amount = parseInt(returnQuantity, 10);

    if (!Number.isFinite(amount) || amount <= 0) {
      showError('Transfer miktarı 0\'dan büyük olmalıdır');
      return;
    }

    if (amount > available) {
      showError(`Araçta yeterli stok yok. Maksimum ${available} transfer edebilirsiniz.`);
      return;
    }

    try {
      setReturnLoading(true);
      await carTransfersAPI.returnToStock({
        carId: selectedReturnCarId,
        productId,
        quantity: amount,
        createdBy: user?.id
      });

      showSuccess('Araç stokları başarıyla geri transfer edildi');
      handleReturnModalClose();
      await fetchProduct();
      await refreshCarStocks();
    } catch (error) {
      console.error('Return stock error:', error);
      showError(error.message || 'Geri transfer sırasında hata oluştu');
    } finally {
      setReturnLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchProduct();
    }
  }, [productId, fetchProduct]);

  // Fetch sales history and stock movements when dates change
  useEffect(() => {
    if (productId && startDate && endDate) {
      fetchSalesHistory();
      fetchStockMovements();
    }
  }, [productId, startDate, endDate, fetchSalesHistory, fetchStockMovements]);

  useEffect(() => {
    if (productId) {
      refreshCarStocks();
    }
  }, [productId, refreshCarStocks]);

  // Initialize edit form data when modal opens
  useEffect(() => {
    if (showEditModal && product) {
      setEditFormData({
        name: product.name || '',
        product_code: product.product_code || '',
        brand: product.brand || '',
        cost: product.cost || '',
        price: product.price || '',
        stock: product.stock || ''
      });
    }
  }, [showEditModal, product]);


  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Ürün bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Ürün Bulunamadı</h2>
          <p className="text-gray-600 mb-4">Aradığınız ürün mevcut değil veya silinmiş olabilir.</p>
          <button
            onClick={() => navigate('/products')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ürünlere Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full">
      {/* Header */}
      <div className="px-2 sm:px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Geri
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{product.name}</h1>
              <p className="text-sm text-gray-500">{product.product_code || 'Ürün Kodu Yok'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Düzenle
            </button>
            <button
              onClick={handleOpenReturnModal}
              disabled={carStockSummary <= 0 || carStockLoading}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white transition-colors ${carStockSummary <= 0 || carStockLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              {carStockLoading ? 'Yükleniyor...' : 'Geri Transfer'}
            </button>
            <button
              onClick={() => setShowSalesModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Satış Yap
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Product Info Cards */}
        <div className="px-2 sm:px-4 py-4 bg-white border-b border-gray-200 flex-shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Mevcut Stok</dt>
                <dd className={`text-lg font-bold ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {product.stock || 0}
                </dd>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                <span className="font-bold text-sm">₺</span>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Satış Fiyatı</dt>
                <dd className="text-lg font-bold text-gray-900">
                  ₺{(parseFloat(product.price) || 0).toFixed(2)}
                </dd>
              </div>
            </div>

            {/* Hide total quantity for warehouse role */}
            {userRole !== 'warehouse' && (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Toplam Satış</dt>
                  <dd className="text-lg font-bold text-gray-900">{salesStats.totalSales}</dd>
                </div>
              </div>
            )}

            {/* Stock Movements Stats */}
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Package className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Stok Giriş</dt>
                <dd className="text-lg font-bold text-green-600">{stockMovementSummary.in}</dd>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <ShoppingCart className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Stok Çıkış</dt>
                <dd className="text-lg font-bold text-red-600">{stockMovementSummary.out}</dd>
              </div>
            </div>

            {/* Hide revenue info for warehouse role */}
            {userRole !== 'warehouse' && (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <span className="font-bold text-sm">₺</span>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Toplam Ciro</dt>
                  <dd className="text-lg font-bold text-gray-900">
                    ₺{salesStats.totalRevenue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </dd>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sales and Stock Movements Section */}
        <div className="flex-1 bg-white flex flex-col min-h-0">
          {/* Filter Section */}
          <div className="px-2 sm:px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <ShoppingCart className="w-5 h-5 mr-2 text-green-600" />
                Satış ve Stok Hareketleri
              </h3>

              {/* Filter Buttons */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'today', label: 'Bugün' },
                  { key: 'week', label: 'Hafta' },
                  { key: 'month', label: 'Ay' },
                  { key: 'custom', label: 'Özel' }
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => handleFilterChange(filter.key)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${salesFilter === filter.key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range Pickers - Only show when 'custom' is selected */}
              {salesFilter === 'custom' && (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <label className="text-xs font-medium text-gray-700">Başlangıç:</label>
                    <DatePicker
                      selected={startDate}
                      onChange={handleStartDateChange}
                      maxDate={endDate}
                      dateFormat="dd/MM/yyyy"
                      locale={tr}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                  <div className="flex items-center space-x-1">
                    <label className="text-xs font-medium text-gray-700">Bitiş:</label>
                    <DatePicker
                      selected={endDate}
                      onChange={handleEndDateChange}
                      minDate={startDate}
                      dateFormat="dd/MM/yyyy"
                      locale={tr}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </div>
              )}

              {/* Date Range Display - Show when not custom */}
              {salesFilter !== 'custom' && (
                <div className="text-sm text-gray-600 font-medium">
                  {startDate.toLocaleDateString('tr-TR')} - {endDate.toLocaleDateString('tr-TR')}
                </div>
              )}
            </div>
          </div>

          {/* Combined Table */}
          <div className="flex-1 overflow-auto">
            {salesLoading || stockMovementsLoading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Veriler yükleniyor...</p>
              </div>
            ) : (
              <table className="w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '15%' }}>Tarih</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '20%' }}>Tür</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '10%' }}>Miktar</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '15%' }}>Birim Fiyat</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '15%' }}>Toplam</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '25%' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    // Combine sales (stock + car sales) and stock movements
                    let combinedData = [
                      ...salesHistory.map(sale => ({
                        id: `sale-${sale.id}`,
                        type: sale.car_id ? 'car_sale' : 'sale',
                        date: new Date(sale.sale_date),
                        quantity: sale.quantity,
                        unitPrice: parseFloat(sale.unit_price || sale.unitPrice),
                        totalPrice: parseFloat(sale.total_price || sale.totalPrice),
                        carId: sale.car_id,
                        data: sale
                      })),
                      ...stockMovements.map(movement => ({
                        id: `movement-${movement.id}`,
                        type: 'movement',
                        date: new Date(movement.created_at),
                        quantity: movement.quantity,
                        movementType: movement.movement_type,
                        referenceType: movement.reference_type,
                        notes: movement.notes,
                        data: movement
                      }))
                    ];

                    // Show all movements including 'out' movements for manual stock adjustments

                    // Sort by date descending
                    combinedData.sort((a, b) => b.date - a.date);

                    if (combinedData.length > 0) {
                      return combinedData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {item.date.toLocaleDateString('tr-TR')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {item.type === 'sale' ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Satış
                              </span>
                            ) : item.type === 'car_sale' ? (
                              <div>
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 whitespace-nowrap">
                                  Araç Satışı
                                </span>
                                {item.data.carPlate && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    {item.data.carPlate}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.movementType === 'in'
                                ? 'bg-blue-100 text-blue-800'
                                : item.movementType === 'out'
                                  ? 'bg-red-100 text-red-800'
                                  : item.movementType === 'transfer_in'
                                    ? 'bg-indigo-100 text-indigo-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}>
                                {item.movementType === 'in' ? 'Stok Giriş' :
                                  item.movementType === 'out' ? 'Stok Çıkış' :
                                    item.movementType === 'transfer_in' ? 'Transfer Giriş' :
                                      item.movementType === 'transfer_out' ? 'Transfer Çıkış' :
                                        item.movementType}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-center">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.quantity}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            {item.type === 'sale' || item.type === 'car_sale' ? (
                              <div className="text-sm text-gray-900 flex items-center justify-end">
                                <span>{item.unitPrice.toFixed(2)}</span>
                                <span className="ml-1">₺</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            {item.type === 'sale' || item.type === 'car_sale' ? (
                              <div className="text-sm font-bold text-green-600 flex items-center justify-end">
                                <span>{item.totalPrice.toFixed(2)}</span>
                                <span className="ml-1">₺</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">
                                {item.referenceType === 'sale' ? 'Satış' :
                                  item.referenceType === 'car_sale' ? 'Araç Satışı' :
                                    item.referenceType === 'transfer' ? 'Transfer' :
                                      item.referenceType === 'car_transfer' ? 'Araç Transferi' :
                                        item.referenceType === 'car_transfer_return' ? 'Araç Transferi' :
                                          item.referenceType === 'manual_adjustment' ? (item.movementType === 'in' ? 'Stok Ekleme' : 'Stok Çıkarma') :
                                            item.referenceType || '-'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-center">
                            {item.type === 'sale' ? (
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleEditSale(item.data)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                                  title="Düzenle"
                                >
                                  <Edit className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteSale(item.data.id)}
                                  disabled={salesSubmitting}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Sil"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ) : item.type === 'car_sale' ? (
                              <div className="flex items-center justify-end space-x-2">
                                <span className="text-xs text-gray-600 mr-2">Araç satışı</span>
                                <button
                                  onClick={() => handleDeleteCarSale(item.data.id)}
                                  disabled={salesSubmitting}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Sil"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ) : item.referenceType === 'manual_adjustment' ? (
                              <div className="flex items-center justify-end space-x-2">
                                <span className="text-xs text-gray-500 max-w-xs truncate mr-2">
                                  {item.notes || '-'}
                                </span>
                                <button
                                  onClick={() => handleDeleteStockMovement(item.data.id)}
                                  disabled={salesSubmitting}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Sil"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500 max-w-xs truncate">
                                {item.notes || '-'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ));
                    } else {
                      return (
                        <tr>
                          <td colSpan="6" className="px-3 py-8 text-center text-gray-500">
                            <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p>Bu dönemde veri bulunmuyor</p>
                          </td>
                        </tr>
                      );
                    }
                  })()}
                </tbody>
              </table>
            )}
          </div>
        </div>


      </div>

      {/* Edit Product Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Package className="h-6 w-6 text-white" />
                  <h2 className="text-xl font-semibold text-white">Ürün Düzenle</h2>
                </div>
                <button
                  type="button"
                  onClick={resetEditForm}
                  className="text-blue-100 hover:text-white transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="max-h-[calc(95vh-120px)] overflow-y-auto">
              <form onSubmit={handleEditSubmit} className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Sol Kolon - Ürün Bilgileri */}
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                        Ürün Bilgileri
                      </h3>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ürün Adı <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={editFormData.name}
                            onChange={handleEditInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ürün Kodu
                          </label>
                          <input
                            type="text"
                            name="product_code"
                            value={editFormData.product_code}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Marka</label>
                          <input
                            type="text"
                            name="brand"
                            value={editFormData.brand}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sağ Kolon - Fiyat ve Stok */}
                  <div className="space-y-6">
                    <div className="bg-green-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                        <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                        {userRole === 'warehouse' ? 'Stok Bilgileri' : 'Fiyat ve Stok Bilgileri'}
                      </h3>
                      <div className="space-y-6">
                        {userRole !== 'warehouse' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Alış Fiyatı (₺)</label>
                              <input
                                type="number"
                                step="0.01"
                                name="cost"
                                value={editFormData.cost}
                                onChange={handleEditInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Satış Fiyatı (₺)</label>
                              <input
                                type="number"
                                step="0.01"
                                name="price"
                                value={editFormData.price}
                                onChange={handleEditInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Mevcut Stok</label>
                          <input
                            type="number"
                            name="stock"
                            value={editFormData.stock}
                            onChange={handleEditInputChange}
                            min="0"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Stok Ekleme */}
                        <div className="bg-blue-100 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Stok Ekle</h4>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={stockToAdd}
                              onChange={(e) => setStockToAdd(e.target.value)}
                              placeholder="Eklenecek miktar"
                              min="0"
                              step="1"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              type="button"
                              onClick={handleAddStock}
                              disabled={!stockToAdd || parseFloat(stockToAdd) <= 0}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              Ekle
                            </button>
                          </div>
                          {stockToAdd && parseFloat(stockToAdd) <= 0 && (
                            <p className="text-xs text-red-600 mt-2">Lütfen 0'dan büyük bir miktar girin</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-8 mt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetEditForm}
                    className="px-8 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <ArrowLeftRight className="w-5 h-5 mr-2 text-indigo-600" />
                Geri Transfer
              </h2>
              <button
                onClick={handleReturnModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {carStockLoading ? (
                <div className="py-12 text-center text-gray-500">
                  <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                  Araç stokları yükleniyor...
                </div>
              ) : carReturnOptions.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                    <Package className="w-6 h-6" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Geri transfer yapılabilecek araç stoku bulunmuyor.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleReturnSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Araç Seçin
                    </label>
                    <select
                      value={selectedReturnCarId}
                      onChange={(e) => {
                        setSelectedReturnCarId(e.target.value);
                        setReturnQuantity('');
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Araç seçin...</option>
                      {carReturnOptions.map(option => (
                        <option key={option.carId} value={option.carId}>
                          {option.plate} — Araç stok: {option.quantity}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transfer Miktarı
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={returnQuantity}
                      onChange={(e) => setReturnQuantity(e.target.value)}
                      disabled={!selectedReturnCarId}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                      placeholder="Miktar girin"
                    />
                    {selectedReturnCarId && (
                      <p className="mt-2 text-sm text-gray-500">
                        Maksimum transfer miktarı: {carReturnOptions.find(option => option.carId === selectedReturnCarId)?.quantity || 0}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                    <span>Toplam araç stoku:</span>
                    <span className="font-semibold text-gray-900">{carStockSummary}</span>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleReturnModalClose}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      disabled={returnLoading}
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={returnLoading}
                      className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {returnLoading ? 'Kaydediliyor...' : 'Geri Transferi Kaydet'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sales Modal */}
      {showSalesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ShoppingCart className="h-6 w-6 text-white" />
                  <h2 className="text-xl font-semibold text-white">
                    {editingSale ? 'Satış Düzenle' : 'Satış Yap'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={resetSalesForm}
                  className="text-green-100 hover:text-white transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="max-h-[calc(95vh-120px)] overflow-y-auto">
              <form onSubmit={handleSalesSubmit} className="p-6">
                <div className="space-y-6">
                  {/* Product Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Ürün Bilgileri</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Ürün:</span>
                        <p className="text-gray-900">{product.name}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Mevcut Stok:</span>
                        <p className="text-gray-900">{product.stock}</p>
                      </div>
                    </div>
                  </div>

                  {/* Sales Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Miktar <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="quantity"
                        value={salesFormData.quantity}
                        onChange={handleSalesInputChange}
                        min="1"
                        max={product.stock}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Satış miktarı"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Birim Fiyat (₺) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">₺</span>
                        </div>
                        <input
                          type="number"
                          name="unitPrice"
                          value={salesFormData.unitPrice}
                          onChange={handleSalesInputChange}
                          step="0.01"
                          min="0"
                          required
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Toplam Fiyat (₺)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">₺</span>
                        </div>
                        <input
                          type="number"
                          name="totalPrice"
                          value={salesFormData.totalPrice}
                          readOnly
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-lg font-semibold text-green-600"
                        />
                      </div>
                    </div>

                  </div>

                  {/* Stock Info */}
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-8 mt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetSalesForm}
                    className="px-8 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={salesSubmitting}
                    className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {salesSubmitting ? 'İşleniyor...' : (editingSale ? 'Satışı Güncelle' : 'Satış Yap')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
